'use client';

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCmsSettings } from '../../services/cmsService';
import { getImageUrl } from '../../config/api';
import './Footer.css';

const defaultFooterData = {
  description: "Handcrafted couture celebrating the timeless majesty of Indian royal heritage and artisanal perfection.",
  copyright: `© ${new Date().getFullYear()} Rangaara Luxury Couture. All rights reserved.`,
  social: {
    instagram: "https://www.instagram.com/rangaaraa?igsh=MWE5bHE0emEwbHkzZg%3D%3D&utm_source=qr",
    facebook: "https://facebook.com/rangaara",
    youtube: "https://youtube.com/rangaara"
  },
  contact: {
    phone: "7023809060",
    email: "Rangaara001@gmail.com"
  }
};

const Footer = () => {
  const location = useLocation();
  const [openSections, setOpenSections] = useState({});
  const [footerData, setFooterData] = useState(defaultFooterData);
  const [logoUrl, setLogoUrl] = useState((process.env.PUBLIC_URL || '') + "/img/RANGAARA-navbar-logo.webp");

  const path = location.pathname;

  // Checkout / Payment funnel on desktop shows a minimal trust footer
  const isCheckoutFunnel = path === '/checkout' || path === '/payment';

  // On Meesho & Flipkart, mobile funnels (Cart, Checkout, Product, Profile, Wishlist, Orders, Auth)
  // NEVER show bulky footers because of sticky buy bars, bottom navs, and focused actions
  const isMobileHiddenRoute = 
    path === '/cart' || 
    path === '/checkout' || 
    path === '/payment' || 
    path.startsWith('/product/') || 
    path === '/profile' || 
    path === '/wishlist' || 
    path.startsWith('/orders') || 
    path === '/login' || 
    path === '/register' || 
    path === '/forgot-password';

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const settings = await getCmsSettings();
        if (settings) {
          if (settings.footer) {
            setFooterData(prev => ({
              ...prev,
              description: settings.footer.description || prev.description,
              copyright: settings.footer.copyright || prev.copyright
            }));
          }
          if (settings.social) {
            setFooterData(prev => ({
              ...prev,
              social: {
                instagram: settings.social.instagram || defaultFooterData.social.instagram,
                facebook: settings.social.facebook || defaultFooterData.social.facebook,
                youtube: settings.social.youtube || defaultFooterData.social.youtube
              }
            }));
          }
          if (settings.contact) {
            setFooterData(prev => ({
              ...prev,
              contact: {
                phone: settings.contact.phone || defaultFooterData.contact.phone,
                email: settings.contact.email || defaultFooterData.contact.email
              }
            }));
          }
          if (settings.logo) {
            setLogoUrl(getImageUrl(settings.logo));
          }
        }
      } catch (err) {
        console.error('Failed to fetch CMS settings in Footer:', err);
      }
    };
    fetchFooter();
  }, []);

  const toggleSection = (sectionId) => {
    if (window.innerWidth <= 768) {
      setOpenSections(prev => ({
        ...prev,
        [sectionId]: !prev[sectionId]
      }));
    }
  };

  const socialLinks = [
    { key: 'instagram', label: 'Instagram', icon: 'fa-brands fa-instagram', url: footerData.social?.instagram || 'https://www.instagram.com/rangaaraa?igsh=MWE5bHE0emEwbHkzZg%3D%3D&utm_source=qr' },
    { key: 'facebook', label: 'Facebook', icon: 'fa-brands fa-facebook-f', url: footerData.social?.facebook || 'https://facebook.com' },
    { key: 'youtube', label: 'YouTube', icon: 'fa-brands fa-youtube', url: footerData.social?.youtube || 'https://youtube.com' }
  ];

  // Minimal Trust Footer for Desktop Checkout Funnel (Flipkart pattern)
  if (isCheckoutFunnel) {
    return (
      <footer className="footer-section footer-checkout-minimal">
        <div className="checkout-footer-container">
          <div className="checkout-trust-item">
            <i className="fa-solid fa-shield-halved"></i>
            <span>100% Safe & Secure Payments</span>
          </div>
          <div className="checkout-trust-item">
            <i className="fa-solid fa-headset"></i>
            <span>Need Help? WhatsApp: <a href="https://wa.me/917023809060" target="_blank" rel="noopener noreferrer">+91 7023809060</a></span>
          </div>
          <p className="checkout-copyright">{footerData.copyright}</p>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`footer-section ${isMobileHiddenRoute ? 'hide-on-mobile-footer' : ''}`}>
      <div className="footer-container">
        {/* Brand Information */}
        <div className="footer-column footer-brand">
          <div className="footer-brand-logo">
            <Link to="/" aria-label="Rangaara Home">
              <img
                src={logoUrl || ((process.env.PUBLIC_URL || '') + "/img/RANGAARA-navbar-logo.webp")}
                alt="Rangaara Luxury Couture"
                className="footer-logo-img"
                width="112"
                height="110"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.target.src = (process.env.PUBLIC_URL || '') + "/img/RANGAARA-removebg-preview (1).png";
                }}
              />
            </Link>
          </div>
          <p className="footer-brand-description">
            {footerData.description}
          </p>
          <div className="footer-support-info">
            <div className="footer-support-item">
              <i className="fa-solid fa-headset"></i>
              <span><strong>Support:</strong> <a href={`tel:${footerData.contact?.phone || '7023809060'}`}>+91 {footerData.contact?.phone || '7023809060'}</a></span>
            </div>
            <div className="footer-support-item">
              <i className="fa-solid fa-envelope"></i>
              <a href={`mailto:${footerData.contact?.email || 'Rangaara001@gmail.com'}`}>{footerData.contact?.email || 'Rangaara001@gmail.com'}</a>
            </div>
          </div>
        </div>

        {/* About Us */}
        <div className="footer-column footer-accordion">
          <div 
            className="footer-column-title footer-accordion-toggle"
            onClick={() => toggleSection('about')}
            aria-expanded={openSections['about']}
            role="button"
            tabIndex={0}
          >
            <span>About Us</span>
            <i className={`fa-solid fa-chevron-down ${openSections['about'] ? 'open' : ''}`}></i>
          </div>
          <div className={`footer-accordion-content ${openSections['about'] ? 'open' : ''}`}>
            <ul className="footer-links">
              <li><Link to="/about">Our Story</Link></li>
              <li><Link to="/about">Craftsmanship & Heritage</Link></li>
            </ul>
          </div>
        </div>

        {/* Help & Policies */}
        <div className="footer-column footer-accordion">
          <div 
            className="footer-column-title footer-accordion-toggle"
            onClick={() => toggleSection('help')}
            aria-expanded={openSections['help']}
            role="button"
            tabIndex={0}
          >
            <span>Help & Policies</span>
            <i className={`fa-solid fa-chevron-down ${openSections['help'] ? 'open' : ''}`}></i>
          </div>
          <div className={`footer-accordion-content ${openSections['help'] ? 'open' : ''}`}>
            <ul className="footer-links">
              <li><Link to="/returns">Return Policy</Link></li>
              <li><Link to="/refund">Refund Policy</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms & Disclaimer</Link></li>
            </ul>
          </div>
        </div>

        {/* Follow Us / Social Media */}
        <div className="footer-column footer-accordion">
          <div 
            className="footer-column-title footer-accordion-toggle"
            onClick={() => toggleSection('social')}
            aria-expanded={openSections['social']}
            role="button"
            tabIndex={0}
          >
            <span>Follow Us</span>
            <i className={`fa-solid fa-chevron-down ${openSections['social'] ? 'open' : ''}`}></i>
          </div>
          <div className={`footer-accordion-content ${openSections['social'] ? 'open' : ''}`}>
            <ul className="footer-social-links">
              {socialLinks.map(item => (
                <li key={item.key}>
                  <a href={item.url} target="_blank" rel="noopener noreferrer" className="social-link">
                    <i className={item.icon}></i>
                    <span>{item.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-bottom-container">
          <p className="copyright-text">{footerData.copyright}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
